import L from "../vendor/leaflet/leaflet.js"
import {pinFlag, zoneCss} from "./ar_zones.js"
import {parseRedactPolygonsJson} from "./ar_redact.js"
import {
  SIM_CAM,
  emitSimCamPlace,
  emitSimPin,
  enToLatLng,
  latLngToEn,
  headingDegFromSimLook,
  mapClickMovesPin,
} from "./sim_cam_geo.js"

function markerIcon(el) {
  return L.icon({
    iconUrl: el.dataset.iconUrl,
    iconRetinaUrl: el.dataset.iconRetinaUrl,
    shadowUrl: el.dataset.shadowUrl,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    tooltipAnchor: [16, -28],
    shadowSize: [41, 41],
  })
}

function parseNumber(value) {
  const number = parseFloat(value)
  return Number.isFinite(number) ? number : null
}

function parsePinList(raw) {
  if (!raw) return []
  try {
    const list = JSON.parse(raw)
    if (!Array.isArray(list)) return []
    return list
      .map((pin) => {
        const lat = parseNumber(pin.lat)
        const lng = parseNumber(pin.lng)
        return lat !== null && lng !== null
          ? {
              lat,
              lng,
              label: pin.label || "",
              cid: pin.cid || "",
              enabled: pin.enabled == null ? true : pinFlag(pin.enabled),
              exclusion_radius_m: pin.exclusion_radius_m,
              activation_radius_m: pin.activation_radius_m,
              detection_radius_m: pin.detection_radius_m,
              beacon_radius_m: pin.beacon_radius_m,
              remote_radius_m: pin.remote_radius_m,
              remote_finite: pin.remote_finite,
            }
          : null
      })
      .filter(Boolean)
  } catch {
    return []
  }
}

const BASEMAP_KEY = "moon-scan-pin-basemap"

function streetsLayer() {
  return L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    maxZoom: 19,
    attribution: "&copy; OpenStreetMap contributors",
  })
}

function satelliteLayer() {
  return L.tileLayer(
    "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    {
      maxZoom: 19,
      attribution:
        "Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community",
    }
  )
}

function addTiles(map, controls) {
  const streets = streetsLayer()
  if (!controls) {
    streets.addTo(map)
    return
  }

  const satellite = satelliteLayer()
  const saved = sessionStorage.getItem(BASEMAP_KEY)
  ;(saved === "satellite" ? satellite : streets).addTo(map)

  const control = L.control.layers(
    {Streets: streets, Satellite: satellite},
    null,
    {collapsed: false, position: "topright"}
  )
  control.addTo(map)
  const box = control.getContainer()
  if (box) box.id = "pin-basemap"

  map.on("baselayerchange", (event) => {
    sessionStorage.setItem(
      BASEMAP_KEY,
      event.name === "Satellite" ? "satellite" : "streets"
    )
  })
}

function ring(layer, latlng, metres, color, fill, fillOpacity) {
  if (!(metres > 0)) return
  L.circle(latlng, {
    radius: metres,
    color,
    weight: 2,
    fillColor: fill,
    fillOpacity,
    interactive: false,
  }).addTo(layer)
}

function drawRadii(layer, latlng, radii) {
  layer.clearLayers()
  if (!latlng) return
  drawRings(layer, latlng, radii)
}

function drawRings(layer, latlng, radii) {
  if (radii.remoteFinite && radii.remote > 0) {
    ring(layer, latlng, radii.remote, zoneCss("remote"), zoneCss("remote"), 0.03)
  }
  ring(layer, latlng, radii.beacon, zoneCss("beacon"), zoneCss("beacon"), 0.04)
  ring(layer, latlng, radii.detection, zoneCss("detection"), zoneCss("detection"), 0.08)
  ring(layer, latlng, radii.activation, zoneCss("activation"), zoneCss("activation"), 0.16)
  ring(layer, latlng, radii.exclusion, zoneCss("exclusion"), zoneCss("exclusion"), 0.12)
}

// Every pin keeps its own cylinders. A multi-pin campaign's pins sit in
// different places with different street scale, so one shared radius set
// would draw a single pin's rings around all of them.
function drawPinRadii(layer, points, fallback) {
  layer.clearLayers()
  for (const point of points) {
    drawRings(layer, [point.lat, point.lng], readPinRadii(point) || fallback)
  }
}

function readPinRadii(pin) {
  if (!pin || pin.exclusion_radius_m == null) return null
  return {
    exclusion: parseNumber(pin.exclusion_radius_m),
    activation: parseNumber(pin.activation_radius_m),
    detection: parseNumber(pin.detection_radius_m),
    beacon: parseNumber(pin.beacon_radius_m),
    remote: parseNumber(pin.remote_radius_m),
    remoteFinite: pinFlag(pin.remote_finite),
  }
}

function readRadii(dataset) {
  return {
    exclusion: parseNumber(dataset.exclusionRadiusM),
    activation: parseNumber(dataset.activationRadiusM),
    detection: parseNumber(dataset.detectionRadiusM),
    beacon: parseNumber(dataset.beaconRadiusM),
    remote: parseNumber(dataset.remoteRadiusM),
    remoteFinite: pinFlag(dataset.remoteFinite),
  }
}

function previewMetres(radii, fitRadiusM) {
  if (fitRadiusM > 0) return fitRadiusM
  if (radii.remoteFinite && radii.remote > 0) return radii.remote
  if (radii.beacon > 0) return radii.beacon
  if (radii.detection > 0) return radii.detection
  return radii.activation
}

function contextBounds(latlng, metres) {
  const contextM = Math.max(metres || 0, 120)
  return L.latLng(latlng).toBounds(contextM * 2.2)
}

// A multi-pin card frames every pin (decision 20261007_multi_location_ar_ux
// §3), plus any redact shapes. Current pin radii (up to 3 km beacons) are
// deliberately not folded in: they would zoom the card out until the
// street-scale rings are a dot.
function previewBoundsForPoints(points, redact) {
  const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]))
  return extendBounds(bounds, redact).pad(0.12)
}

function parseRedactPolygons(dataset) {
  return parseRedactPolygonsJson(dataset.redactPolygons)
}

function redactLatLngs(poly) {
  const ring = poly && poly.polygon && poly.polygon.coordinates && poly.polygon.coordinates[0]
  if (!Array.isArray(ring)) return []
  return ring
    .map((pair) => {
      const lng = Number(pair && pair[0])
      const lat = Number(pair && pair[1])
      return Number.isFinite(lat) && Number.isFinite(lng) ? L.latLng(lat, lng) : null
    })
    .filter(Boolean)
}

function redactBounds(polygons) {
  const pts = []
  for (const poly of polygons) pts.push(...redactLatLngs(poly))
  return pts.length ? L.latLngBounds(pts) : null
}

function extendBounds(base, extra) {
  if (base && extra) return L.latLngBounds(base).extend(extra)
  return extra || base
}

function drawRedactPolygons(layer, polygons) {
  for (const poly of polygons) {
    const latlngs = redactLatLngs(poly)
    if (latlngs.length < 3) continue
    L.polygon(latlngs, {
      color: poly.color || "#7f1d1d",
      weight: 2,
      fillOpacity: 0.22,
      interactive: false,
    }).addTo(layer)
  }
}

const CAM_ICON_PX = 64

function cameraIcon(headingDeg) {
  return L.divIcon({
    className: "pin-sim-cam",
    iconSize: [CAM_ICON_PX, CAM_ICON_PX],
    iconAnchor: [CAM_ICON_PX / 2, CAM_ICON_PX / 2],
    html: `<div style="width:${CAM_ICON_PX}px;height:${CAM_ICON_PX}px;border-radius:50%;background:rgba(17,24,39,0.45);border:3px solid #f59e0b;box-sizing:border-box;display:flex;align-items:center;justify-content:center;cursor:grab;box-shadow:0 2px 10px rgba(0,0,0,0.45)">
      <div data-sim-cam-rot style="width:40px;height:40px;transform:rotate(${headingDeg}deg);transform-origin:50% 50%;line-height:0">
        <svg viewBox="0 0 40 40" width="40" height="40" aria-hidden="true">
          <polygon points="20,2 34,36 20,28 6,36" fill="#fbbf24" stroke="#111827" stroke-width="2" stroke-linejoin="round"/>
        </svg>
      </div>
    </div>`,
  })
}

function styleCamIconEl(el) {
  if (!el) return
  el.style.background = "transparent"
  el.style.border = "none"
}

export const PinMap = {
  mounted() {
    const lat = parseNumber(this.el.dataset.lat)
    const lng = parseNumber(this.el.dataset.lng)
    // data-pins present (even "[]") is the enabled-pin list. Do not fall back
    // to Event lat/lng — that would draw a disabled pin.
    const listedPins = this.el.hasAttribute("data-pins")
      ? parsePinList(this.el.dataset.pins)
      : null
    const interactive = this.el.dataset.interactive !== "false"
    const controls = interactive || this.el.dataset.controls === "true"
    const points =
      listedPins !== null
        ? listedPins
        : lat !== null && lng !== null
          ? [{lat, lng, label: ""}]
          : []
    const hasPoint = points.length > 0
    const multi = points.length > 1
    const center = hasPoint
      ? [points[0].lat, points[0].lng]
      : lat !== null && lng !== null
        ? [lat, lng]
        : [37.9838, 23.7275]
    this.radii = readRadii(this.el.dataset)
    this.icon = markerIcon(this.el)
    const redactPolygons = parseRedactPolygons(this.el.dataset)
    // A listed pin's own cylinders beat the page-level attributes: the admin
    // card map now sends them per pin, and the single-pin frame must agree
    // with the rings drawn under that pin.
    const primaryRadii = (hasPoint && readPinRadii(points[0])) || this.radii
    this.previewBounds =
      hasPoint && !interactive
        ? multi
          ? previewBoundsForPoints(points, redactBounds(redactPolygons))
          : extendBounds(
              contextBounds(center, previewMetres(primaryRadii, parseNumber(this.el.dataset.fitRadiusM))),
              redactBounds(redactPolygons),
            )
        : null

    this.map = L.map(this.el, {
      zoomControl: controls,
      scrollWheelZoom: controls,
      doubleClickZoom: controls,
      boxZoom: controls,
      keyboard: controls,
      attributionControl: controls,
    })

    if (this.previewBounds) {
      this.map.fitBounds(this.previewBounds, {padding: [20, 20], maxZoom: 17, animate: false})
    } else {
      this.map.setView(center, lat !== null && lng !== null ? 16 : 12)
    }

    addTiles(this.map, controls)
    this.redactLayer = L.layerGroup().addTo(this.map)
    drawRedactPolygons(this.redactLayer, redactPolygons)
    this.radiiLayer = L.layerGroup().addTo(this.map)
    this.extraMarkers = []
    this.marker = null
    if (interactive && listedPins !== null) {
      // form pins are drawn after the simCam pane exists
    } else if (multi) {
      for (const point of points) {
        this.extraMarkers.push(L.marker([point.lat, point.lng], {icon: this.icon}).addTo(this.map))
      }
      drawPinRadii(this.radiiLayer, points, this.radii)
    } else {
      this.marker = hasPoint ? L.marker(center, {icon: this.icon}).addTo(this.map) : null
      if (hasPoint) drawRadii(this.radiiLayer, center, primaryRadii)
    }
    this.camEn = {east: 0, north: 0}
    this.camYaw = 0
    this.camPitch = 0
    this.camDragging = false
    this.camSuppressClick = false
    this.camMarker = null

    if (interactive) {
      this.map.createPane("simCam")
      this.map.getPane("simCam").style.zIndex = 650

      this.map.on("click", (event) => {
        if (
          !mapClickMovesPin(event, {
            camDragging: this.camDragging,
            suppressClick: this.camSuppressClick,
          })
        ) {
          this.camSuppressClick = false
          return
        }
        const {lat: pickedLat, lng: pickedLng} = event.latlng
        this.place(pickedLat, pickedLng)
        this.pushEvent("map_pick", {lat: pickedLat, lng: pickedLng})
      })

      this.handleEvent("pin_coords", ({lat: nextLat, lng: nextLng}) => {
        const parsedLat = parseNumber(nextLat)
        const parsedLng = parseNumber(nextLng)
        if (parsedLat !== null && parsedLng !== null) {
          this.place(parsedLat, parsedLng)
        }
      })

      this.handleEvent("pin_radii", (payload) => {
        this.radii = {
          exclusion: parseNumber(payload.exclusion),
          activation: parseNumber(payload.activation),
          detection: parseNumber(payload.detection),
          beacon: parseNumber(payload.beacon),
          remote: parseNumber(payload.remote),
          remoteFinite: pinFlag(payload.remote_finite),
        }
        const here = this.marker?.getLatLng()
        if (here) drawRadii(this.radiiLayer, here, this.radii)
      })

      this.handleEvent("form_pins", ({pins, selected_cid: selectedCid}) => {
        const next = Array.isArray(pins)
          ? pins
              .map((pin) => {
                const lat = parseNumber(pin.lat)
                const lng = parseNumber(pin.lng)
                return lat !== null && lng !== null
                  ? {
                      lat,
                      lng,
                      label: pin.label || "",
                      cid: pin.cid || "",
                      enabled: pin.enabled == null ? true : pinFlag(pin.enabled),
                    }
                  : null
              })
              .filter(Boolean)
          : []
        this.syncFormPins(next, selectedCid)
      })

      this.onSimCam = (event) => {
        const east = Number(event.detail?.east)
        const north = Number(event.detail?.north)
        const yaw = Number(event.detail?.yaw)
        const pitch = Number(event.detail?.pitch)
        if (!Number.isFinite(east) || !Number.isFinite(north)) return
        this.camEn = {east, north}
        if (Number.isFinite(yaw)) this.camYaw = yaw
        this.camPitch = Number.isFinite(pitch) ? pitch : 0
        if (!this.camDragging) this.syncCamMarker()
      }
      window.addEventListener(SIM_CAM, this.onSimCam)
      if (listedPins !== null) {
        this.syncFormPins(points, this.el.dataset.selectedCid)
      } else {
        this.syncCamMarker()
      }
    }

    this.fittedPreview = false
    this.syncView = () => {
      this.map.invalidateSize()
      if (!this.previewBounds) return
      if (controls && this.fittedPreview) return
      this.map.fitBounds(this.previewBounds, {padding: [20, 20], maxZoom: 17, animate: false})
      if (controls) {
        const size = this.map.getSize()
        if (size.x > 0 && size.y > 0) this.fittedPreview = true
      }
    }

    this.resizeObserver = new ResizeObserver(() => this.syncView())
    this.resizeObserver.observe(this.el)
    requestAnimationFrame(() => this.syncView())
  },

  syncFormPins(points, selectedCid) {
    if (this.extraMarkers) {
      for (const marker of this.extraMarkers) this.map.removeLayer(marker)
    }
    this.extraMarkers = []
    const selected = points.find((point) => point.cid && point.cid === selectedCid) || points[0]
    for (const point of points) {
      if (selected && point.lat === selected.lat && point.lng === selected.lng && point.cid === selected.cid) {
        continue
      }
      const marker = L.marker([point.lat, point.lng], {
        icon: this.icon,
        opacity: point.enabled === false ? 0.45 : 0.85,
      }).addTo(this.map)
      marker.on("click", (event) => {
        L.DomEvent.stopPropagation(event)
        if (point.cid) this.pushEvent("select_pin", {cid: point.cid})
      })
      this.extraMarkers.push(marker)
    }
    if (selected) {
      this.place(selected.lat, selected.lng)
    } else if (this.marker) {
      this.map.removeLayer(this.marker)
      this.marker = null
      if (this.radiiLayer) this.radiiLayer.clearLayers()
    }
  },

  place(lat, lng) {
    const latlng = [lat, lng]
    if (this.marker) {
      this.marker.setLatLng(latlng)
    } else {
      this.marker = L.marker(latlng, {icon: this.icon}).addTo(this.map)
    }
    if (this.radiiLayer) drawRadii(this.radiiLayer, latlng, this.radii)
    this.map.panTo(latlng)
    this.syncCamMarker()
    emitSimPin(lat, lng)
  },

  pinOrigin() {
    const here = this.marker?.getLatLng()
    return here ? {lat: here.lat, lng: here.lng} : null
  },

  syncCamMarker() {
    if (this.el.dataset.interactive === "false") return
    const origin = this.pinOrigin()
    if (!origin) return
    const {lat, lng} = enToLatLng(origin.lat, origin.lng, this.camEn.east, this.camEn.north)
    const heading = headingDegFromSimLook(this.camYaw, this.camPitch)
    if (this.camMarker) {
      if (!this.camDragging) this.camMarker.setLatLng([lat, lng])
      const rot = this.camMarker.getElement()?.querySelector("[data-sim-cam-rot]")
      if (rot) rot.style.transform = `rotate(${heading}deg)`
      return
    }
    this.camMarker = L.marker([lat, lng], {
      icon: cameraIcon(heading),
      draggable: true,
      autoPan: false,
      keyboard: false,
      bubblingMouseEvents: false,
      pane: "simCam",
      zIndexOffset: 800,
      title: "Simulator camera — drag the disc to walk. Map click still sets the pin.",
    }).addTo(this.map)
    styleCamIconEl(this.camMarker.getElement())
    this.camMarker.on("mousedown", (event) => L.DomEvent.stopPropagation(event))
    this.camMarker.on("click", (event) => {
      L.DomEvent.stopPropagation(event)
      this.camSuppressClick = true
    })
    this.camMarker.on("dragstart", () => {
      this.camDragging = true
      this.camSuppressClick = true
    })
    this.camMarker.on("drag", (event) => {
      const originNow = this.pinOrigin()
      if (!originNow) return
      const ll = event.target.getLatLng()
      const en = latLngToEn(originNow.lat, originNow.lng, ll.lat, ll.lng)
      this.camEn = en
      emitSimCamPlace(en)
    })
    this.camMarker.on("dragend", () => {
      this.camDragging = false
      this.camSuppressClick = true
    })
  },

  destroyed() {
    if (this.onSimCam) window.removeEventListener(SIM_CAM, this.onSimCam)
    if (this.resizeObserver) this.resizeObserver.disconnect()
    if (this.map) this.map.remove()
  },
}
